import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Spinner } from 'react-bootstrap';
import { saveKpis } from '../services/docSenseService';

/**
 * KpiReviewTable
 *
 * Tab 1 — "Extracted KPIs" (read-only): AI output.
 * Tab 2 — "Final KPIs" (editable): edit, add, remove, reset, bulk-save.
 *
 * Props:
 *   onCancelReview — if provided (KPIs already saved), shows a Cancel button
 *                    so user can return to the saved view without re-saving.
 */
const KpiReviewTable = ({
  documentId,
  extractedKpis,
  extractedLoading,
  savedKpis,
  savedLoading,
  onSaved,
  onCancelReview,
}) => {
  const [activeTab, setActiveTab] = useState('extracted');
  const [finalRows, setFinalRows] = useState([]);
  const seededRef = useRef(false);

  // Seed finalRows once: prefer extracted, fallback to saved
  useEffect(() => {
    if (seededRef.current) return;
    const src = !extractedLoading && extractedKpis.length > 0
      ? extractedKpis
      : !savedLoading && savedKpis.length > 0
        ? savedKpis.map(k => ({ ...k, kpiId: k.kpiId ?? k.id }))
        : null;
    if (src) { setFinalRows(toFinalRows(src)); seededRef.current = true; }
  }, [extractedKpis, extractedLoading, savedKpis, savedLoading]);

  // ── Edit ──
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});

  const fmt = (v) => {
    if (v === undefined || v === null) return "—";
    if (typeof v !== "number") return v;

    const rounded = Number(v.toFixed(2));
    const isInteger = Number.isInteger(rounded);

    // if (rounded >= 1_000_000) {
    //   const kValue   = rounded / 1_000;
    //   const kRounded = Number(kValue.toFixed(2));
    //   const isKInt   = Number.isInteger(kRounded);
    //   return (
    //     new Intl.NumberFormat("en-IN", {
    //       minimumFractionDigits: isKInt ? 0 : 2,
    //       maximumFractionDigits: 2,
    //     }).format(kRounded) + "K"
    //   );
    // }

    return new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: isInteger ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(rounded);
  };

  const startEdit = (row) => {
    setEditingId(row._id);
    setEditValues({
      kpiTitle: row.kpiTitle ?? '',
      value: fmt(row.value) ?? '',
      unit: row.unit ?? '',
      scope: row.scope ?? '',
      scopeCategory: row.scopeCategory ?? '',
      periodStartDate: row.periodStartDate ? String(row.periodStartDate).slice(0, 10) : '',
      periodEndDate: row.periodEndDate ? String(row.periodEndDate).slice(0, 10) : '',
    });
  };

  const cancelEdit = () => { setEditingId(null); setEditValues({}); };

  const commitEdit = (row) => {
    setFinalRows(prev => prev.map(r =>
      r._id === row._id ? {
        ...r,
        kpiTitle: editValues.kpiTitle || r.kpiTitle,
        value: editValues.value !== '' ? fmt(editValues.value) : fmt(r.value),
        unit: editValues.unit,
        scope: editValues.scope || null,
        scopeCategory: editValues.scopeCategory || null,
        periodStartDate: editValues.periodStartDate || null,
        periodEndDate: editValues.periodEndDate || null,
      } : r
    ));
    cancelEdit();
  };

  // ── Add / Remove ──
  const addRow = () => {
    const newRow = {
      _id: `new-${Date.now()}`, kpiId: null,
      kpiTitle: '', value: '', unit: '',
      scope: null, scopeCategory: null,
      periodStartDate: null, periodEndDate: null,
    };
    setFinalRows(prev => [...prev, newRow]);
    setEditingId(newRow._id);
    setEditValues({ kpiTitle: '', value: '', unit: '', scope: '', scopeCategory: '', periodStartDate: '', periodEndDate: '' });
  };

  const removeRow = (id) => { setFinalRows(prev => prev.filter(r => r._id !== id)); if (editingId === id) cancelEdit(); };

  // ── Reset ──
  const handleReset = () => {
    const src = extractedKpis.length > 0
      ? extractedKpis
      : savedKpis.map(k => ({ ...k, kpiId: k.kpiId ?? k.id }));
    setFinalRows(toFinalRows(src));
    cancelEdit();
  };

  // ── Save ──
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const handleSaveAll = useCallback(async () => {
    if (!finalRows.length) return;
    setSaving(true); setSaveError(null);
    try {
      await saveKpis(documentId, finalRows.map(r => ({
        kpiId: r.kpiId || undefined,
        kpiTitle: r.kpiTitle,
        value: r.value !== '' && r.value != null ? Number(r.value) : null,
        unit: r.unit || null,
        scope: r.scope || null,
        scopeCategory: r.scopeCategory || null,
        periodStartDate: r.periodStartDate || null,
        periodEndDate: r.periodEndDate || null,
      })));
      onSaved?.();
    } catch (e) {
      console.error(e);
      setSaveError('Failed to save KPIs. Please try again.');
    } finally { setSaving(false); }
  }, [documentId, finalRows, onSaved]);

  // ── Search ──
  const [search, setSearch] = useState('');
  const q = search.toLowerCase();
  const filteredExtracted = extractedKpis.filter(k => !q || k.kpiTitle?.toLowerCase().includes(q) || k.rawTitle?.toLowerCase().includes(q));
  const filteredFinal = finalRows.filter(r => !q || r.kpiTitle?.toLowerCase().includes(q));

  return (
    <div style={{ background: 'white', border: '1.5px solid #e2eaf0', borderRadius: 12, overflow: 'hidden', marginBottom: 20 }}>

      {/* Tab bar + toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 20px', borderBottom: '1.5px solid #e2eaf0', background: 'white',
        flexWrap: 'wrap', gap: 8,
      }}>
        <div style={{ display: 'flex' }}>
          {[
            { key: 'extracted', label: 'Extracted KPIs', count: extractedKpis.length },
            { key: 'final', label: 'Final KPIs', count: finalRows.length },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); setSearch(''); }}
              style={{
                background: 'none', border: 'none',
                borderBottom: activeTab === tab.key ? '2.5px solid #3f88a5' : '2.5px solid transparent',
                padding: '14px 18px 12px', fontSize: '0.875rem',
                fontWeight: activeTab === tab.key ? 700 : 500,
                color: activeTab === tab.key ? '#3f88a5' : '#6b8794',
                cursor: 'pointer', transition: 'all 0.15s',
                display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
              }}
            >
              {tab.label}
              <span style={{
                background: activeTab === tab.key ? 'rgba(63,136,165,0.12)' : 'rgba(0,0,0,0.06)',
                color: activeTab === tab.key ? '#3f88a5' : '#888',
                borderRadius: 10, padding: '1px 7px', fontSize: '0.72rem', fontWeight: 600,
              }}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 0', flexWrap: 'wrap' }}>
          {/* Search */}
          <div style={{ position: 'relative' }}>
            <i className="fa fa-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ab4bf', fontSize: '0.75rem' }} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search KPIs..."
              style={{ paddingLeft: 30, paddingRight: 10, paddingTop: 6, paddingBottom: 6, border: '1.5px solid #e2eaf0', borderRadius: 7, fontSize: '0.83rem', color: '#1a2e38', outline: 'none', width: 180 }}
            />
          </div>

          {activeTab === 'final' && (
            <>
              <GhostBtn onClick={handleReset} disabled={extractedKpis.length === 0 && savedKpis.length === 0} title="Reset from extracted KPIs">
                <i className="fa fa-refresh" /> Reset
              </GhostBtn>
              <button onClick={addRow} style={addBtnStyle}>
                <i className="fa fa-plus" /> Add KPI
              </button>
              <button
                onClick={handleSaveAll}
                disabled={saving || !finalRows.length}
                style={{
                  background: saving || !finalRows.length ? '#b0c4ce' : '#3f88a5',
                  border: 'none', color: 'white', fontWeight: 600, fontSize: '0.875rem',
                  borderRadius: 7, padding: '6px 16px',
                  cursor: saving || !finalRows.length ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap',
                }}
              >
                {saving
                  ? <><Spinner as="span" animation="border" size="sm" style={{ width: 13, height: 13 }} /> Saving...</>
                  : <><i className="fa fa-check-circle" /> Save All ({finalRows.length})</>
                }
              </button>
            </>
          )}

          {/* Cancel review — only when user entered from "already saved" state */}
          {onCancelReview && (
            <GhostBtn onClick={onCancelReview}>
              <i className="fa fa-times" /> Cancel
            </GhostBtn>
          )}
        </div>
      </div>

      {/* Save error */}
      {saveError && (
        <div style={{ background: 'rgba(231,76,60,0.08)', color: '#e74c3c', padding: '10px 20px', fontSize: '0.85rem', borderBottom: '1px solid rgba(231,76,60,0.15)' }}>
          <i className="fa fa-exclamation-circle me-2" />{saveError}
        </div>
      )}

      {activeTab === 'extracted' && <ExtractedTab kpis={filteredExtracted} isLoading={extractedLoading} />}
      {activeTab === 'final' && (
        <FinalTab
          rows={filteredFinal} savedKpis={savedKpis}
          editingId={editingId} editValues={editValues} setEditValues={setEditValues}
          onStartEdit={startEdit} onCommitEdit={commitEdit}
          onCancelEdit={cancelEdit} onRemove={removeRow}
        />
      )}
    </div>
  );
};

// ── Extracted tab ─────────────────────────────────────────────────────────────
const ExtractedTab = ({ kpis, isLoading }) => {
  if (isLoading) return <LoadingState label="Loading extracted KPIs..." />;
  if (!kpis.length) return <EmptyState icon="🔍" message="No KPIs were extracted from this document." />;

  const matchCfg = {
    strong: { bg: 'rgba(39,174,96,0.1)', color: '#27ae60', label: 'Strong' },
    weak: { bg: 'rgba(230,126,34,0.1)', color: '#e67e22', label: 'Weak' },
    needs_review: { bg: 'rgba(231,76,60,0.08)', color: '#e74c3c', label: 'Review' },
  };

  const fmt = (d) => { try { return d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : ''; } catch { return d; } };

  return (
    <div>
      <THead
        cols={['KPI', 'Match', 'Value', 'Unit', 'Scope', 'Scope Cat.', 'Period']}
        grid="2.2fr 0.7fr 0.8fr 0.7fr 0.8fr 0.9fr 1.4fr"
      />
      {kpis.map((k, idx) => {
        const mc = matchCfg[k.matchStrength] || matchCfg.needs_review;
        return (
          <div key={k.id} style={{ display: 'grid', gridTemplateColumns: '2.2fr 0.7fr 0.8fr 0.7fr 0.8fr 0.9fr 1.4fr', padding: '12px 20px', alignItems: 'center', borderBottom: idx < kpis.length - 1 ? '1px solid rgba(0,0,0,0.04)' : 'none', background: idx % 2 === 0 ? 'white' : '#fafcfd' }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.875rem', color: '#1a2e38' }}>{k.kpiTitle || '—'}</div>
              {k.rawTitle && k.rawTitle !== k.kpiTitle && <div style={{ fontSize: '0.73rem', color: '#8ba9b5', fontStyle: 'italic', marginTop: 2 }}>"{k.rawTitle}"</div>}
              {k.remark && <div style={{ fontSize: '0.73rem', color: '#a0b4be', marginTop: 2 }} title={k.remark}><i className="fa fa-info-circle me-1" />{k.remark.length > 70 ? `${k.remark.slice(0, 70)}…` : k.remark}</div>}
            </div>
            <div><span style={{ background: mc.bg, color: mc.color, borderRadius: 4, padding: '2px 8px', fontSize: '0.73rem', fontWeight: 600 }}>{mc.label}</span></div>
            <div style={{ fontSize: '0.875rem', fontWeight: 600, color: k.value != null ? '#1a2e38' : '#b0c4ce' }}>{k.value ?? '—'}</div>
            <div style={{ fontSize: '0.82rem', color: k.unit ? '#6b8794' : '#b0c4ce' }}>{k.unit || '—'}</div>
            <div style={{ fontSize: '0.82rem', color: k.scope ? '#6b8794' : '#b0c4ce' }}>{k.scope || '—'}</div>
            <div style={{ fontSize: '0.82rem', color: k.scopeCategory ? '#6b8794' : '#b0c4ce' }}>{k.scopeCategory || '—'}</div>
            <div style={{ fontSize: '0.8rem', color: '#6b8794' }}>{k.periodStartDate || k.periodEndDate ? `${fmt(k.periodStartDate)} – ${fmt(k.periodEndDate)}` : <span style={{ color: '#b0c4ce' }}>—</span>}</div>
          </div>
        );
      })}
    </div>
  );
};

// ── Final tab ─────────────────────────────────────────────────────────────────
const FinalTab = ({ rows, savedKpis, editingId, editValues, setEditValues, onStartEdit, onCommitEdit, onCancelEdit, onRemove }) => {
  if (!rows.length) return <EmptyState icon="📋" message='No KPIs yet. Click "Add KPI" above or use Reset to import from extracted KPIs.' />;
  return (
    <div>
      {savedKpis?.length > 0 && (
        <div style={{ background: 'rgba(240,165,0,0.07)', borderBottom: '1px solid rgba(240,165,0,0.2)', padding: '10px 20px', fontSize: '0.83rem', color: '#b07800', display: 'flex', alignItems: 'center', gap: 8 }}>
          <i className="fa fa-exclamation-triangle" />
          You have {savedKpis.length} previously saved KPIs. Saving again will overwrite them.
        </div>
      )}
      <THead
        cols={['KPI Title', 'Value', 'Unit', 'Scope', 'Scope Cat.', 'Period Start', 'Period End', '']}
        grid="2fr 0.8fr 0.7fr 0.8fr 0.9fr 1fr 1fr 90px"
      />
      {rows.map((row, idx) => (
        <FinalRow
          key={row._id} row={row}
          isEditing={editingId === row._id}
          editValues={editValues} setEditValues={setEditValues}
          onStartEdit={() => onStartEdit(row)} onCommitEdit={() => onCommitEdit(row)}
          onCancelEdit={onCancelEdit} onRemove={() => onRemove(row._id)}
          isEven={idx % 2 === 0}
        />
      ))}
    </div>
  );
};

const FinalRow = ({ row, isEditing, editValues, setEditValues, onStartEdit, onCommitEdit, onCancelEdit, onRemove, isEven }) => {
  const fmt = (d) => { try { return d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'; } catch { return d || '—'; } };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '2fr 0.8fr 0.7fr 0.8fr 0.9fr 1fr 1fr 90px', padding: '10px 20px', alignItems: 'center', borderBottom: '1px solid rgba(0,0,0,0.04)', background: isEditing ? 'rgba(63,136,165,0.04)' : isEven ? 'white' : '#fafcfd', transition: 'background 0.15s' }}>
      <div>{isEditing ? <input value={editValues.kpiTitle} onChange={e => setEditValues(p => ({ ...p, kpiTitle: e.target.value }))} placeholder="KPI name" style={{ ...ei, width: '95%' }} /> : <span style={{ fontWeight: 600, fontSize: '0.875rem', color: row.kpiTitle ? '#1a2e38' : '#b0c4ce' }}>{row.kpiTitle || 'Untitled KPI'}</span>}</div>
      <div>{isEditing ? <input type="number" value={editValues.value} onChange={e => setEditValues(p => ({ ...p, value: e.target.value }))} placeholder="Value" style={{ ...ei, width: '90%' }} /> : <span style={{ fontSize: '0.875rem', fontWeight: 600, color: row.value != null && row.value !== '' ? '#1a2e38' : '#b0c4ce' }}>{row.value != null && row.value !== '' ? row.value : '—'}</span>}</div>
      <div>{isEditing ? <input value={editValues.unit} onChange={e => setEditValues(p => ({ ...p, unit: e.target.value }))} placeholder="Unit" style={{ ...ei, width: 70 }} /> : <span style={{ fontSize: '0.82rem', color: row.unit ? '#6b8794' : '#b0c4ce' }}>{row.unit || '—'}</span>}</div>
      <div>{isEditing ? <input value={editValues.scope} onChange={e => setEditValues(p => ({ ...p, scope: e.target.value }))} placeholder="Scope" style={{ ...ei, width: '90%' }} /> : <span style={{ fontSize: '0.82rem', color: row.scope ? '#6b8794' : '#b0c4ce' }}>{row.scope || '—'}</span>}</div>
      <div>{isEditing ? <input value={editValues.scopeCategory} onChange={e => setEditValues(p => ({ ...p, scopeCategory: e.target.value }))} placeholder="Category" style={{ ...ei, width: '90%' }} /> : <span style={{ fontSize: '0.82rem', color: row.scopeCategory ? '#6b8794' : '#b0c4ce' }}>{row.scopeCategory || '—'}</span>}</div>
      <div>{isEditing ? <input type="date" value={editValues.periodStartDate} onChange={e => setEditValues(p => ({ ...p, periodStartDate: e.target.value }))} style={{ ...ei, width: '95%' }} /> : <span style={{ fontSize: '0.8rem', color: row.periodStartDate ? '#6b8794' : '#b0c4ce' }}>{fmt(row.periodStartDate)}</span>}</div>
      <div>{isEditing ? <input type="date" value={editValues.periodEndDate} onChange={e => setEditValues(p => ({ ...p, periodEndDate: e.target.value }))} style={{ ...ei, width: '95%' }} /> : <span style={{ fontSize: '0.8rem', color: row.periodEndDate ? '#6b8794' : '#b0c4ce' }}>{fmt(row.periodEndDate)}</span>}</div>
      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
        {isEditing ? (
          <><Ico icon="fa-check" color="#27ae60" bg="rgba(39,174,96,0.1)" border="rgba(39,174,96,0.2)" onClick={onCommitEdit} /><Ico icon="fa-times" color="#6b8794" bg="rgba(0,0,0,0.04)" border="#e2eaf0" onClick={onCancelEdit} /></>
        ) : (
          <><Ico icon="fa-pencil" color="#3f88a5" bg="rgba(63,136,165,0.08)" border="rgba(63,136,165,0.2)" onClick={onStartEdit} /><Ico icon="fa-trash" color="#e74c3c" bg="rgba(231,76,60,0.06)" border="rgba(231,76,60,0.15)" onClick={onRemove} /></>
        )}
      </div>
    </div>
  );
};

// ── Small shared pieces ───────────────────────────────────────────────────────
const THead = ({ cols, grid }) => (
  <div style={{ display: 'grid', gridTemplateColumns: grid, padding: '9px 20px', background: '#f7fafc', borderBottom: '1.5px solid #e2eaf0' }}>
    {cols.map(h => <div key={h} style={{ fontSize: '0.73rem', fontWeight: 700, color: '#6b8794', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</div>)}
  </div>
);

const Ico = ({ icon, color, bg, border, onClick }) => (
  <button onClick={onClick} style={{ background: bg, border: `1.5px solid ${border}`, color, borderRadius: 6, padding: '5px 8px', fontSize: '0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <i className={`fa ${icon}`} />
  </button>
);

const GhostBtn = ({ children, onClick, disabled, title }) => (
  <button
    onClick={onClick} disabled={disabled} title={title}
    style={{ background: 'transparent', border: '1.5px solid #e2eaf0', color: '#6b8794', borderRadius: 7, padding: '6px 12px', fontSize: '0.82rem', fontWeight: 500, cursor: disabled ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.15s' }}
    onMouseEnter={(e) => { if (!disabled) { e.currentTarget.style.borderColor = '#3f88a5'; e.currentTarget.style.color = '#3f88a5'; } }}
    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2eaf0'; e.currentTarget.style.color = '#6b8794'; }}
  >
    {children}
  </button>
);

const LoadingState = ({ label }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', gap: 12, color: '#6b8794' }}>
    <Spinner animation="border" size="sm" style={{ color: '#3f88a5' }} /><span style={{ fontSize: '0.875rem' }}>{label}</span>
  </div>
);

const EmptyState = ({ icon, message }) => (
  <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6b8794' }}>
    <div style={{ fontSize: '2.5rem', opacity: 0.3, marginBottom: 12 }}>{icon}</div>
    <p style={{ margin: 0, fontSize: '0.875rem' }}>{message}</p>
  </div>
);

const toFinalRows = (kpis) => kpis.map((k, i) => ({
  _id: `ext-${k.id ?? i}`, kpiId: k.kpiId ?? null,
  kpiTitle: k.kpiTitle ?? '', value: k.value ?? '', unit: k.unit ?? '',
  scope: k.scope ?? null, scopeCategory: k.scopeCategory ?? null,
  periodStartDate: k.periodStartDate ?? null, periodEndDate: k.periodEndDate ?? null,
}));

const ei = { border: '1.5px solid #3f88a5', borderRadius: 6, padding: '4px 8px', fontSize: '0.82rem', color: '#1a2e38', outline: 'none', background: 'white' };
const addBtnStyle = { background: 'rgba(63,136,165,0.08)', border: '1.5px solid rgba(63,136,165,0.2)', color: '#3f88a5', borderRadius: 7, padding: '6px 12px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 };

export default KpiReviewTable;